import { Suspense } from 'react';
import { isNotFound } from '@tanstack/react-router';
import { useTranslation } from '@/lib/i18n';
import { usePortalCampaignQuery, usePortalMetadataQuery } from '@/lib/queries';
import { categories, type CategoryKey, type Category } from '@worldcoo/monolith/core';
import type { I18n } from '@/lib/i18n';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { TZDate } from '@date-fns/tz';

const categoryNumberToKey = Object.fromEntries(
	Object.entries(categories).map(([key, value]) => [value, key]),
) as Record<Category, CategoryKey>;

export function CampaignMetadata(props: { campaignId: string }) {
	return (
		<Suspense>
			<SuspensedCampaignMetadata campaignId={props.campaignId} />
		</Suspense>
	);
}

function SuspensedCampaignMetadata(props: { campaignId: string }) {
	const { i18n } = useTranslation();
	const campaign = usePortalCampaignQuery(props.campaignId);
	const metadata = usePortalMetadataQuery();

	if (isNotFound(campaign) || isNotFound(metadata)) return null;
	if (!metadata.showCampaignDetails) return null;

	const categoryLabels = campaign.data.categories
		.map((cat) => categoryNumberToKey[cat])
		.filter(Boolean)
		.map((key) => i18n.categories[key]);

	const hasAnyInfo =
		categoryLabels.length > 0 || campaign.data.cityName || campaign.data.startDate || campaign.data.endDate;
	if (!hasAnyInfo) return null;

	return (
		<DivWithPrimaryColorVariable className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
			{categoryLabels.length > 0 && (
				<span className="flex items-center gap-1.5">
					<CategoryIcon className="text-primary shrink-0" />
					{categoryLabels.join(', ')}
				</span>
			)}

			{campaign.data.cityName && (
				<span className="flex items-center gap-1.5">
					<CityIcon className="text-primary shrink-0" />
					{campaign.data.cityName}
				</span>
			)}

			{(campaign.data.startDate || campaign.data.endDate) && (
				<DateRange startDate={campaign.data.startDate} endDate={campaign.data.endDate} i18n={i18n} />
			)}
		</DivWithPrimaryColorVariable>
	);
}

function DateRange(props: { startDate: string | undefined; endDate: string | undefined; i18n: I18n }) {
	const formatDate = (date: string) => {
		// const d = new Date(date);
		const d = new TZDate(date, 'Europe/Madrid');

		const day = String(d.getDate()).padStart(2, '0');
		const month = String(d.getMonth() + 1).padStart(2, '0');
		const year = String(d.getFullYear()).slice(2);
		return `${day}/${month}/${year}`;
	};

	return (
		<span className="flex items-center gap-1.5">
			<CalendarIcon className="text-primary shrink-0" />
			{props.startDate && (
				<>
					{props.i18n.campaign.from} <strong>{formatDate(props.startDate)}</strong>
				</>
			)}
			{props.endDate && (
				<>
					{props.i18n.campaign.until} <strong>{formatDate(props.endDate)}</strong>
				</>
			)}
		</span>
	);
}

function CategoryIcon(props: React.SVGProps<SVGSVGElement>) {
	return (
		<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
			<path
				d="M9.15859 10.87C9.05859 10.86 8.93859 10.86 8.82859 10.87C6.44859 10.79 4.55859 8.84 4.55859 6.44C4.55859 3.99 6.53859 2 8.99859 2C11.4486 2 13.4386 3.99 13.4386 6.44C13.4286 8.84 11.5386 10.79 9.15859 10.87Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				opacity="0.4"
				d="M16.4112 4C18.3512 4 19.9112 5.57 19.9112 7.5C19.9112 9.39 18.4113 10.93 16.5413 11C16.4613 10.99 16.3713 10.99 16.2812 11"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M4.15875 14.56C1.73875 16.18 1.73875 18.82 4.15875 20.43C6.90875 22.27 11.4188 22.27 14.1688 20.43C16.5888 18.81 16.5888 16.17 14.1688 14.56C11.4288 12.73 6.91875 12.73 4.15875 14.56Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				opacity="0.4"
				d="M18.3398 20C19.0598 19.85 19.7398 19.56 20.2998 19.13C21.8598 17.96 21.8598 16.03 20.2998 14.86C19.7498 14.44 19.0798 14.16 18.3698 14"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

function CityIcon(props: React.SVGProps<SVGSVGElement>) {
	return (
		<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
			<path
				opacity="0.4"
				d="M10.9989 13.4295C12.722 13.4295 14.1189 12.0326 14.1189 10.3095C14.1189 8.58633 12.722 7.18945 10.9989 7.18945C9.27578 7.18945 7.87891 8.58633 7.87891 10.3095C7.87891 12.0326 9.27578 13.4295 10.9989 13.4295Z"
				stroke="currentColor"
				strokeWidth="1.5"
			/>
			<path
				d="M2.62166 8.49C4.59166 -0.169998 17.4217 -0.159997 19.3817 8.5C20.5317 13.58 17.3717 17.88 14.6017 20.54C12.5917 22.48 9.41166 22.48 7.39166 20.54C4.63166 17.88 1.47166 13.57 2.62166 8.49Z"
				stroke="currentColor"
				strokeWidth="1.5"
			/>
		</svg>
	);
}

function CalendarIcon(props: React.SVGProps<SVGSVGElement>) {
	return (
		<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
			<path
				d="M7 2V5"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeMiterlimit="10"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M15 2V5"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeMiterlimit="10"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				opacity="0.4"
				d="M2.5 9.08997H19.5"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeMiterlimit="10"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M20 8.5V17C20 20 18.5 22 15 22H7C3.5 22 2 20 2 17V8.5C2 5.5 3.5 3.5 7 3.5H15C18.5 3.5 20 5.5 20 8.5Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeMiterlimit="10"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				opacity="0.4"
				d="M14.6937 13.6992H14.7027"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				opacity="0.4"
				d="M14.6937 16.6992H14.7027"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				opacity="0.4"
				d="M10.9945 13.6992H11.0035"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				opacity="0.4"
				d="M10.9945 16.6992H11.0035"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				opacity="0.4"
				d="M7.29529 13.6992H7.30427"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				opacity="0.4"
				d="M7.29297 16.6992H7.30195"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}
